import * as v from 'valibot'

export const NOVEL_DRAMA_LOCATION_PRESET_LIMIT = 20

const Id = v.pipe(v.string(), v.regex(/^[a-zA-Z0-9][a-zA-Z0-9_-]{0,79}$/),
  v.check(value => !['constructor', 'prototype', '__proto__'].includes(value)))

/** New drafts only author locations. The optional default is retained for frozen historical sources. */
export const NovelDramaPresets = v.strictObject({
  default_state_id: v.optional(Id),
  locations: v.array(v.strictObject({ location_id: Id, state_id: Id })),
})

const list = value => Array.isArray(value) ? value : []
const has_field = person => Boolean(person && typeof person === 'object' && Object.hasOwn(person, 'drama_presets'))
const has_image = item => typeof item?.image === 'string' && item.image.trim().length > 0
const pictured_states = person => list(person?.performance_states).filter(has_image)
const valid_state = (person, state_id) => pictured_states(person).find(state => state.id === state_id)

/** Presence opts into the explicit policy, including incomplete configurations. Never migrates legacy content. */
export function hasNovelDramaPresets(pack) {
  return list(pack?.characters).some(person => person?.id !== pack?.player_id && has_field(person))
}

/** Pure authoring validation. System-provided card defaults never require author configuration. */
export function novelDramaPresetIssues(pack) {
  const issues = []
  const locations = list(pack?.locations)
  let location_count = 0
  const add = (code, path) => issues.push({ code, path })
  for (const [index, person] of list(pack?.characters).entries()) {
    const base = `characters.${index}.drama_presets`
    if (person?.id === pack?.player_id) {
      if (has_field(person)) add('drama_presets_npc_only', base)
      continue
    }
    if (!has_field(person)) continue
    const presets = person.drama_presets
    location_count += list(presets?.locations).length
    const parsed = v.safeParse(NovelDramaPresets, presets)
    if (!parsed.success) {
      const suffix = parsed.issues[0]?.path?.map(item => item.key).join('.')
      add('drama_presets_invalid', suffix ? `${base}.${suffix}` : base)
      continue
    }
    if (Object.hasOwn(presets, 'default_state_id') && !valid_state(person, presets.default_state_id))
      add('drama_default_state_invalid', `${base}.default_state_id`)
    const selected_locations = new Set()
    for (const [position, preset] of presets.locations.entries()) {
      const path = `${base}.locations.${position}`
      if (selected_locations.has(preset.location_id)) add('drama_location_duplicate', `${path}.location_id`)
      selected_locations.add(preset.location_id)
      const location = locations.find(item => item?.id === preset.location_id)
      if (!location) add('drama_location_invalid', `${path}.location_id`)
      else if (!has_image(location)) add('drama_location_image_required', `${path}.location_id`)
      if (!valid_state(person, preset.state_id)) add('drama_location_state_invalid', `${path}.state_id`)
    }
  }
  if (location_count > NOVEL_DRAMA_LOCATION_PRESET_LIMIT) add('drama_location_limit_exceeded', 'characters')
  return issues
}

function selection(person, state_id, background = null) {
  const state = valid_state(person, state_id)
  if (!state) return null
  return { character_id: person.id, state_id, background_id: background?.id ?? null,
    character_image: state.image, background_image: background?.image ?? null }
}

function default_selection(person) {
  // The old field belongs to immutable versions/runs and must retain its original image and identity.
  if (Object.hasOwn(person.drama_presets || {}, 'default_state_id'))
    return selection(person, person.drama_presets.default_state_id)
  if (!has_image(person)) return null
  return { character_id: person.id, state_id: 'character-card', background_id: null,
    character_image: person.image, background_image: null }
}

/** Enumerates only explicit choices. Missing/invalid authoring input cannot trigger a Cartesian expansion. */
export function novelDramaPresetSelections(pack) {
  if (novelDramaPresetIssues(pack).length) return []
  return list(pack?.characters).filter(person => person?.id !== pack.player_id).flatMap(person => [
    default_selection(person),
    ...list(person.drama_presets?.locations).map(preset => selection(person, preset.state_id,
      list(pack.locations).find(location => location.id === preset.location_id))),
  ]).filter(Boolean)
}

/** The narrative state never selects a different preset; an unlisted location uses this actor's default. */
export function resolveNovelDramaPreset(pack, { character_id, location_id } = {}) {
  if (novelDramaPresetIssues(pack).length) return null
  const person = list(pack?.characters).find(item => item?.id === character_id && item.id !== pack.player_id)
  if (!person) return null
  const preset = list(person.drama_presets?.locations).find(item => item.location_id === location_id)
  return preset ? selection(person, preset.state_id, list(pack.locations).find(location => location.id === preset.location_id))
    : default_selection(person)
}
